import type {
  ClinicalCareGuidelineDetail,
  ClinicalCareGuidelineDetailResponse,
  ClinicalCareGuidelineListItem,
  ClinicalCareGuidelinesListResponse,
  ClinicalCareGuidelineLookupMatch,
  ClinicalCareGuidelineRevisionResponse,
  ClinicalCareGuidelinesLookupResponse,
} from "../types/clinical-care-guidelines";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object";

/** Support both `{ data: payload }` and a flat decrypted Arc+ payload. */
export const unwrapArcplusPayload = <T extends object>(
  payload: unknown
): T | null => {
  if (!isRecord(payload)) return null;

  if ("success" in payload || "guidelines" in payload || "guideline" in payload || "matches" in payload) {
    return payload as T;
  }

  if (isRecord(payload.data)) {
    return payload.data as T;
  }

  return null;
};

export const extractClinicalCareGuidelinesList = (
  payload: unknown
): ClinicalCareGuidelinesListResponse => {
  const empty: ClinicalCareGuidelinesListResponse = {
    success: false,
    guidelines: [],
    hasMore: false,
    nextCursor: null,
    count: 0,
    categories: [],
  };

  const data = unwrapArcplusPayload<ClinicalCareGuidelinesListResponse>(payload);
  if (!data) return empty;

  return {
    success: Boolean(data.success),
    error: data.error,
    guidelines: Array.isArray(data.guidelines) ? data.guidelines : [],
    hasMore: Boolean(data.hasMore),
    nextCursor: data.nextCursor ?? null,
    count: Number(data.count ?? data.guidelines?.length ?? 0),
    categories: Array.isArray(data.categories) ? data.categories : [],
  };
};

export const extractClinicalCareGuidelineDetail = (
  payload: unknown
): ClinicalCareGuidelineDetail | null => {
  const data = unwrapArcplusPayload<ClinicalCareGuidelineDetailResponse>(payload);
  if (!data?.success || !data.guideline) return null;
  return data.guideline;
};

export const extractClinicalCareGuidelineRevision = (
  payload: unknown
): ClinicalCareGuidelineRevisionResponse["guideline"] | null => {
  const data =
    unwrapArcplusPayload<ClinicalCareGuidelineRevisionResponse>(payload);
  if (!data?.success || !data.guideline?.revision) return null;
  return data.guideline;
};

export const extractClinicalCareGuidelinesLookup = (
  payload: unknown
): ClinicalCareGuidelinesLookupResponse => {
  const empty: ClinicalCareGuidelinesLookupResponse = {
    success: false,
    matches: [],
    count: 0,
    matchedCount: 0,
  };

  const data =
    unwrapArcplusPayload<ClinicalCareGuidelinesLookupResponse>(payload);
  if (!data) return empty;

  const matches: ClinicalCareGuidelineLookupMatch[] = Array.isArray(data.matches)
    ? data.matches
    : [];

  return {
    success: Boolean(data.success),
    error: data.error,
    count: Number(data.count ?? matches.length),
    matchedCount: Number(
      data.matchedCount ?? matches.filter((match) => match.matched).length
    ),
    matches,
  };
};

export const formatGuidelineDateTime = (value?: string | null): string => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export const uniqueConditionLabels = (labels: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];

  labels.forEach((label) => {
    const trimmed = label?.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(trimmed);
  });

  return unique.slice(0, 20);
};

export const fhirConditionLabel = (resource: unknown): string | null => {
  if (!isRecord(resource)) return null;

  const code = isRecord(resource.code) ? resource.code : null;
  const text = typeof code?.text === "string" ? code.text.trim() : "";
  if (text) return text;

  const coding = code && Array.isArray(code.coding) ? code.coding : [];
  for (const entry of coding) {
    if (isRecord(entry) && typeof entry.display === "string" && entry.display.trim()) {
      return entry.display.trim();
    }
  }

  return null;
};

export const labelsFromFhirConditionBundle = (bundle: unknown): string[] => {
  if (!isRecord(bundle)) return [];

  const entries = Array.isArray(bundle.entry) ? bundle.entry : [];
  const labels = entries.map((entry) => {
    if (!isRecord(entry)) return null;
    return fhirConditionLabel(entry.resource);
  });

  if (isRecord(bundle.resource) || bundle.resourceType === "Condition") {
    labels.push(fhirConditionLabel(bundle));
  }

  return uniqueConditionLabels(labels);
};

export const listItemAuthor = (item: ClinicalCareGuidelineListItem): string =>
  item.latestRevision?.createdByName?.trim() || "Unknown";

export const listItemRevisionNumber = (
  item: ClinicalCareGuidelineListItem
): number =>
  item.latestRevision?.revisionNumber ?? item.latestRevisionNumber ?? 0;
